import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * NRS (Numeric Rating Scale) Component - eCOA Consortium Compliant
 * 
 * Implements Numeric Rating Scale according to FDA PRO Guidance and eCOA standards:
 * - Numbers representing ordered continuum (typically 0-10)
 * - Verbal anchors at extreme ends
 * - Optional verbal anchor at intermediate positions
 * - Equidistant button spacing (all buttons same size regardless of label length)
 * - Visual indicators (arrows) connecting anchors to endpoint numbers
 * - Minimum font size enforcement
 * - No scrolling required (entire scale visible)
 * 
 * Reference: eCOA Consortium Response Scales Documentation
 */
// Anchor labels may carry a single <br> from the source instrument to control
// where the text wraps (e.g. "Worst <br>Imaginable" under the top of an NRS).
// Split on it and render real line breaks; everything else stays plain text so
// no other markup can slip through.
const renderAnchor = (text) => {
  const parts = String(text ?? '').split(/<br\s*\/?>/i);
  // The common case has no <br>. Return the bare string rather than wrapping it,
  // so the label stays a direct text child of the anchor cell instead of gaining
  // a wrapper element between the text and the grid cell.
  if (parts.length === 1) return parts[0].trim();
  return parts.map((part, i, arr) => (
    <span key={i}>
      {part.trim()}
      {i < arr.length - 1 ? <br /> : null}
    </span>
  ));
};

const NRSScale = ({
  minValue = 0,
  maxValue = 10,
  leftAnchor = "No pain",
  rightAnchor = "Pain as bad as you can imagine",
  middleAnchor = null, // Optional middle anchor (e.g., "Moderate pain" at value 5)
  middleAnchorValue = null,
  value = null,
  onChange,
  disabled = false,
  orientation = 'horizontal', // 'horizontal' or 'vertical'
  // Where the end anchors sit relative to the numbers (horizontal only):
  //   'below'       - under the end numbers, centered on the tick (default)
  //   'below-edges' - under the ends, aligned to the outer edges of the scale
  //   'sides'       - on the same row, to the left and right of the numbers
  //   'custom'      - free placement: each anchor is offset in px from its tick
  anchorPlacement = 'below',
  // Only used by 'custom'. Pixel offsets from the anchor's default spot
  // (centered under its end number), so 0,0 looks exactly like 'below'.
  leftAnchorOffset = { x: 0, y: 0 },
  rightAnchorOffset = { x: 0, y: 0 },
  // Custom Layout promotes the anchors to their own draggable sub-elements, so
  // the scale block must stop painting its own or they render twice.
  hideAnchors = false,
  showIndicators = true, // Show visual indicator arrows
  colorGradient = false, // Apply color gradient to buttons (green → yellow → red)
  hideMetadata = false, // Hide eCOA compliance metadata (for preview/participant view)
  className = ""
}) => {
  const [selectedValue, setSelectedValue] = useState(value);

  // Keep internal state in sync with the value prop (prevents stale display on re-visit/resume)
  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  // Generate array of numbers from min to max
  const numbers = [];
  for (let i = minValue; i <= maxValue; i++) {
    numbers.push(i);
  }

  const handleSelect = (val) => {
    if (disabled) return;
    setSelectedValue(val);
    if (onChange) {
      onChange(val);
    }
  };

  // Minimum sizes as per eCOA standards
  const MIN_BUTTON_SIZE = 44; // px - minimum tap target size
  const MIN_FONT_SIZE = 14; // px - minimum readable font size
  // Anchors are one column wide and centered on their own tick. Sizing them to
  // their text instead puts an endpoint label half outside the scale, and the
  // builder canvas clips at the element edge (CanvasElement's overflow-hidden
  // inner), so a long label like "Worst Imaginable" gets cut mid-word. Wrapping
  // inside the column keeps it under its number and always fully visible.
  // 'below-edges' trades that off: the label is pinned to the outer edge of the
  // scale so it can run wider (and inward, under its neighbour) on one line.
  const ANCHOR_EDGE_MAX_WIDTH = 150;
  const trackColumnCount = numbers.length;
  const trackGridTemplateColumns = `repeat(${trackColumnCount}, minmax(${MIN_BUTTON_SIZE}px, 1fr))`;
  const endpointLineOffset = `calc(50% / ${trackColumnCount})`;
  const getTrackColumnForValue = (anchorValue) => {
    const index = numbers.findIndex((num) => num === anchorValue);
    return String(index >= 0 ? index + 1 : Math.ceil(trackColumnCount / 2));
  };

  // Color gradient helper: returns background + text colors for a button based on position
  const getGradientStyle = (num) => {
    if (!colorGradient) return {};
    const range = maxValue - minValue;
    const ratio = range > 0 ? (num - minValue) / range : 0; // 0 = left, 1 = right
    // Green (120°) → Yellow (60°) → Red (0°)
    const hue = Math.round(120 * (1 - ratio));
    const isSelected = selectedValue === num;
    return {
      backgroundColor: isSelected ? `hsl(${hue}, 70%, 40%)` : `hsl(${hue}, 60%, 85%)`,
      color: isSelected ? '#ffffff' : `hsl(${hue}, 50%, 25%)`,
      borderColor: isSelected ? `hsl(${hue}, 70%, 30%)` : `hsl(${hue}, 40%, 70%)`
    };
  };

  if (orientation === 'vertical') {
    return (
      <div className={`nrs-scale-vertical flex flex-col items-center ${className}`}>
        {/* Top Anchor with Indicator */}
        <div className="flex flex-col items-center mb-2">
          <span className="text-sm font-medium text-gray-700 text-center mb-1 max-w-[200px]">
            {rightAnchor}
          </span>
          {showIndicators && (
            <ChevronDown className="w-5 h-5 text-blue-600 animate-pulse" />
          )}
        </div>

        {/* Number Buttons - Equidistant */}
        <div className="flex flex-col-reverse gap-2">
          {numbers.map((num) => (
            <button
              key={num}
              onClick={() => handleSelect(num)}
              disabled={disabled}
              className={`nrs-button transition-all duration-200 ${
                colorGradient
                  ? (selectedValue === num ? 'shadow-lg dark:shadow-gray-900/20 scale-105' : '')
                  : (selectedValue === num
                    ? 'bg-blue-600 text-white border-blue-700 shadow-lg dark:shadow-gray-900/20 scale-105'
                    : 'bg-white dark:bg-gray-800 text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50')
              } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              style={{
                width: `${MIN_BUTTON_SIZE}px`,
                height: `${MIN_BUTTON_SIZE}px`,
                minWidth: `${MIN_BUTTON_SIZE}px`, // Enforce equidistant spacing
                minHeight: `${MIN_BUTTON_SIZE}px`,
                fontSize: `${MIN_FONT_SIZE}px`,
                fontWeight: '600',
                borderWidth: '2px',
                borderStyle: 'solid',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                ...getGradientStyle(num)
              }}
              aria-label={`Select ${num}`}
            >
              {num}
            </button>
          ))}
        </div>

        {/* Bottom Anchor with Indicator */}
        <div className="flex flex-col items-center mt-2">
          {showIndicators && (
            <ChevronUp className="w-5 h-5 text-blue-600 animate-pulse" />
          )}
          <span className="text-sm font-medium text-gray-700 text-center mt-1 max-w-[200px]">
            {leftAnchor}
          </span>
        </div>

        {/* eCOA Compliance Notice */}
        {!hideMetadata && process.env.NODE_ENV === 'development' && (
          <div className="mt-3 text-xs text-gray-400 font-mono text-center">
            NRS: Equidistant spacing • {MIN_BUTTON_SIZE}px buttons • {MIN_FONT_SIZE}px font
          </div>
        )}
      </div>
    );
  }

  // Horizontal orientation (default)
  const buttonRow = (extraClass) => (
        <div
          className={extraClass}
          style={{
            display: 'grid',
            gridTemplateColumns: trackGridTemplateColumns,
            width: '100%',
            alignItems: 'center',
          }}
        >
          {numbers.map((num) => (
            <button
              key={num}
              onClick={() => handleSelect(num)}
              disabled={disabled}
              className={`nrs-button transition-all duration-200 ${
                colorGradient
                  ? (selectedValue === num ? 'shadow-lg dark:shadow-gray-900/20 scale-105' : '')
                  : (selectedValue === num
                    ? 'bg-blue-600 text-white border-blue-700 shadow-lg dark:shadow-gray-900/20 scale-105'
                    : 'bg-white dark:bg-gray-800 text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50')
              } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              style={{
                width: `${MIN_BUTTON_SIZE}px`,
                height: `${MIN_BUTTON_SIZE}px`,
                minWidth: `${MIN_BUTTON_SIZE}px`, // CRITICAL: Enforce equidistant spacing
                minHeight: `${MIN_BUTTON_SIZE}px`,
                fontSize: `${MIN_FONT_SIZE}px`,
                fontWeight: '600',
                borderWidth: '2px',
                borderStyle: 'solid',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                justifySelf: 'center',
                ...getGradientStyle(num)
              }}
              aria-label={`Select ${num}`}
            >
              {num}
            </button>
          ))}
        </div>
  );

  // Anchors are positioned separately (Custom Layout): paint the numbers only.
  if (hideAnchors) {
    return (
      <div className={`nrs-scale-horizontal flex flex-col items-center w-full ${className}`}>
        {buttonRow('')}
      </div>
    );
  }

  // Free placement: anchors are taken out of flow and offset from the centre of
  // their end tick, so they can sit anywhere without pushing the scale around.
  // They stay on one line here — the author controls where they land.
  if (anchorPlacement === 'custom') {
    // 0,0 pins each anchor to its end of the scale rather than to the centre of
    // the end tick. Centring would push half a long label past the element edge,
    // and the builder canvas clips there — so the default would arrive already
    // cut off. From the edge, the label is always fully visible and the author
    // moves it inward (negative X on the right, positive X on the left).
    const freeAnchor = (text, side, offset) => (
      <span
        className="absolute text-sm font-medium text-gray-700 leading-tight whitespace-nowrap"
        style={{
          [side]: 0,
          top: `${8 + (Number(offset?.y) || 0)}px`,
          transform: `translateX(${Number(offset?.x) || 0}px)`,
        }}
      >
        {renderAnchor(text)}
      </span>
    );
    return (
      <div className={`nrs-scale-horizontal flex flex-col items-center w-full ${className}`}>
        <div className="w-full">
          {buttonRow('mb-3')}
          {/* Reserved strip so the absolutely placed anchors still take up
              room in the element instead of overlapping what follows. */}
          <div className="relative w-full" style={{ minHeight: '40px' }}>
            {freeAnchor(leftAnchor, 'left', leftAnchorOffset)}
            {freeAnchor(rightAnchor, 'right', rightAnchorOffset)}
          </div>
        </div>
      </div>
    );
  }

  // Anchors beside the numbers: gives a long label the whole side of the
  // element, so it stays on one line instead of wrapping under its tick.
  if (anchorPlacement === 'sides') {
    const sideAnchor = (text, align) => (
      <span
        className={`text-sm font-medium text-gray-700 leading-tight flex-shrink-0 ${align}`}
        style={{ maxWidth: '40%' }}
      >
        {renderAnchor(text)}
      </span>
    );
    return (
      <div className={`nrs-scale-horizontal flex items-center gap-3 w-full ${className}`}>
        {sideAnchor(leftAnchor, 'text-right')}
        <div className="flex-1 min-w-0">{buttonRow('')}</div>
        {sideAnchor(rightAnchor, 'text-left')}
      </div>
    );
  }

  const edgeAligned = anchorPlacement === 'below-edges';

  return (
    <div className={`nrs-scale-horizontal flex flex-col items-center w-full ${className}`}>
      {/* Number Buttons Row with Anchors */}
      <div className="w-full flex flex-col items-center">
        {buttonRow('mb-3')}

        {/* Anchors with Visual Indicators */}
        <div className="w-full relative">
          {/* Connection Line */}
          {showIndicators && (
            <div
              className="absolute top-0 h-0.5 bg-gray-300"
              style={{
                top: '-8px',
                left: endpointLineOffset,
                right: endpointLineOffset,
              }}
            />
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: trackGridTemplateColumns,
              width: '100%',
              alignItems: 'start',
            }}
          >
            {/* Left Anchor — centered on its own tick, exactly one column wide.
                Aligning to the column edge instead pushed the label off the tick
                (the button is only 44px inside a much wider column) and let long
                labels run under the neighbouring number; w-full keeps them
                wrapping inside their own column. */}
            <div
              className={`flex flex-col relative ${edgeAligned ? 'items-start' : 'items-center'}`}
              style={{ gridColumn: '1', minWidth: 0 }}
            >
              {showIndicators && (
                <div
                  className="absolute -top-4"
                  style={{ left: '50%', transform: 'translateX(-50%)' }}
                >
                  <div className="w-0.5 h-4 bg-blue-600"></div>
                  <div className="w-3 h-3 -mt-1.5 -ml-1 border-2 border-blue-600 bg-blue-600 transform rotate-45"></div>
                </div>
              )}
              <span
                className={`text-sm font-medium text-gray-700 leading-tight pt-2 ${
                  edgeAligned ? 'text-left' : 'w-full text-center'}`}
                data-nrs-anchor="left"
                style={edgeAligned ? { width: 'max-content', maxWidth: `${ANCHOR_EDGE_MAX_WIDTH}px` } : undefined}
              >
                {renderAnchor(leftAnchor)}
              </span>
            </div>

            {/* Middle Anchor (if provided) */}
            {middleAnchor && middleAnchorValue !== null && (
              <div
                className="flex flex-col items-center relative"
                style={{ gridColumn: getTrackColumnForValue(middleAnchorValue), minWidth: 0 }}
              >
                {showIndicators && (
                  <div className="absolute -top-4">
                    <div className="w-0.5 h-4 bg-gray-500 mx-auto"></div>
                    <div className="w-2 h-2 -mt-1 -ml-0.5 border-2 border-gray-500 bg-gray-500 transform rotate-45"></div>
                  </div>
                )}
                <span className="text-xs font-medium text-gray-600 text-center leading-tight pt-2">
                  {middleAnchor}
                </span>
              </div>
            )}

            {/* Right Anchor — centered on its own tick, exactly one column wide
                (see the left anchor above). */}
            <div
              className={`flex flex-col relative ${edgeAligned ? 'items-end' : 'items-center'}`}
              style={{ gridColumn: String(trackColumnCount), minWidth: 0 }}
            >
              {showIndicators && (
                <div
                  className="absolute -top-4"
                  style={{ left: '50%', transform: 'translateX(-50%)' }}
                >
                  <div className="w-0.5 h-4 bg-blue-600 mx-auto"></div>
                  <div className="w-3 h-3 -mt-1.5 -ml-1 border-2 border-blue-600 bg-blue-600 transform rotate-45"></div>
                </div>
              )}
              <span
                className={`text-sm font-medium text-gray-700 leading-tight pt-2 ${
                  edgeAligned ? 'text-right' : 'w-full text-center'}`}
                data-nrs-anchor="right"
                style={edgeAligned ? { width: 'max-content', maxWidth: `${ANCHOR_EDGE_MAX_WIDTH}px` } : undefined}
              >
                {renderAnchor(rightAnchor)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Selected Value Display (optional, for testing) */}
      {!hideMetadata && selectedValue !== null && process.env.NODE_ENV === 'development' && (
        <div className="mt-3 px-3 py-1 bg-gray-100 dark:bg-gray-700 border border-gray-300 rounded text-xs text-gray-600 font-mono">
          Selected: {selectedValue}
        </div>
      )}

      {/* eCOA Compliance Notice */}
      {!hideMetadata && process.env.NODE_ENV === 'development' && (
        <div className="mt-3 text-xs text-gray-400 font-mono text-center">
          NRS: Equidistant {MIN_BUTTON_SIZE}×{MIN_BUTTON_SIZE}px buttons • {MIN_FONT_SIZE}px font • Visual indicators
        </div>
      )}
    </div>
  );
};

export default NRSScale;
